import { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import dayjs from 'dayjs';

import { BreadcrumbHeader, FilterTabs } from '../../components/shared';
import { purchaseOrders, supplierPayments, shippingCosts, ticketCosts, partOrders } from '../../services/dataClient';
import '../../styles/design-tokens.css';

import FinancialReportsTab from './tabs/FinancialReportsTab';
import ShippingCostsTab from './tabs/ShippingCostsTab';
import CostPerTicketTab from './tabs/CostPerTicketTab';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Constants ────────────────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";const TABS = [
{ label: 'Financial Reports', value: 'financialReports' },
{ label: 'Shipping Costs', value: 'shippingCosts' },
{ label: 'Cost per Ticket', value: 'costPerTicket' }];

const PRESETS = [
{ label: 'This Month', value: 'thisMonth' },
{ label: 'Last Month', value: 'lastMonth' },
{ label: 'Last 3 Months', value: 'last3' },
{ label: 'Last 6 Months', value: 'last6' },
{ label: 'Year to Date', value: 'ytd' },
{ label: 'Custom Range', value: 'custom' }];

function computeRange(preset, customStart, customEnd) {
  const today = dayjs();
  switch (preset) {
    case 'thisMonth':return { start: today.startOf('month').format('YYYY-MM-DD'), end: today.endOf('month').format('YYYY-MM-DD') };
    case 'lastMonth':{
        const lm = today.subtract(1, 'month');
        return { start: lm.startOf('month').format('YYYY-MM-DD'), end: lm.endOf('month').format('YYYY-MM-DD') };
      }
    case 'last3':return { start: today.subtract(3, 'month').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
    case 'last6':return { start: today.subtract(6, 'month').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
    case 'ytd':return { start: today.startOf('year').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
    case 'custom':
      return {
        start: customStart || today.startOf('month').format('YYYY-MM-DD'),
        end: customEnd || today.format('YYYY-MM-DD')
      };
    default:return { start: today.startOf('month').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
  }
}

const inRange = (dateStr, start, end) =>
Boolean(dateStr) && dateStr >= start && dateStr <= end;

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('financialReports');
  const [preset, setPreset] = useState('thisMonth');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const { start: startDate, end: endDate } = useMemo(
    () => computeRange(preset, customStart, customEnd),
    [preset, customStart, customEnd]
  );

  const filteredPos = useMemo(
    () => purchaseOrders.filter((p) => inRange(p.date, startDate, endDate)),
    [startDate, endDate]
  );

  const filteredPayments = useMemo(
    () => supplierPayments.filter((p) => inRange(p.dueDate, startDate, endDate)),
    [startDate, endDate]
  );

  const filteredShipping = useMemo(
    () => shippingCosts.filter((s) => inRange(s.billedDate, startDate, endDate)),
    [startDate, endDate]
  );

  const enrichedTickets = useMemo(
    () =>
    ticketCosts.map((tc) => {
      const order = partOrders.find((o) => o.id === tc.partOrderId);
      return {
        ...tc,
        partNumber: order?.partNumber ?? '—',
        customerName: order?.customerName ?? '—',
        fulfillmentMethod: order?.fulfillmentMethod ?? '—',
        date: order?.slaDueDate ?? null
      };
    }),
    []
  );

  const filteredTickets = useMemo(
    () => enrichedTickets.filter((t) => !t.date || inRange(t.date, startDate, endDate)),
    [enrichedTickets, startDate, endDate]
  );

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <BreadcrumbHeader
        title="Reports"
        crumbs={[
        { label: 'Accounting' },
        { label: 'Reports' }]
        } />

      {/* ── Tab strip card ──────────────────────────────────────────────────── */}
      <ContentCard
        sx={{ px: 3,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          '& > div:first-of-type': { mb: 0 }
        }}>
        
        <FilterTabs tabs={TABS} value={activeTab} onChange={setActiveTab} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, alignSelf: 'center' }}>
          <Select
            size="small"
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            sx={{ minWidth: 155, fontSize: '0.875rem', ...fieldSx['& .MuiOutlinedInput-root'], '& .MuiSelect-select': { paddingTop: '5px', paddingBottom: '5px' } }}>
            
            {PRESETS.map((p) =>
            <MenuItem key={p.value} value={p.value} sx={{ fontSize: '0.875rem' }}>
                {p.label}
              </MenuItem>
            )}
          </Select>
          {preset === 'custom' &&
          <>
              <TextField
              size="small" type="date" label="From" value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 145, ...fieldSx }} />
            
              <TextField
              size="small" type="date" label="To" value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 145, ...fieldSx }} />
            
            </>
          }
        </Box>
      </ContentCard>

      {/* ── Content card ────────────────────────────────────────────────────── */}
      <ContentCard
        sx={{ overflow: 'hidden',
          pt: 2
        }}>
        
        {activeTab === 'financialReports' &&
        <FinancialReportsTab
          filteredPos={filteredPos}
          filteredPayments={filteredPayments}
          filteredShipping={filteredShipping}
          filteredTickets={filteredTickets}
          startDate={startDate}
          endDate={endDate} />

        }
        {activeTab === 'shippingCosts' &&
        <ShippingCostsTab shippingCosts={filteredShipping} />
        }
        {activeTab === 'costPerTicket' &&
        <CostPerTicketTab ticketCosts={filteredTickets} />
        }
      </ContentCard>
    </Box>);

}